import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "@components/layout";
import showToast from "@utils/toast";
import {
  fetchLessonById,
  fetchLessonsByLevel,
  languageMap,
} from "src/service/activities.services";
import { postRequest } from "src/service/api-services/api-services";
import { API_URL, userRole } from "src/service/config";
import { CreateLessonApiResponse, LessonFormState } from "src/types";
import { Trash2 } from "lucide-react";
import {
  currentLanguage,
  currentLevelInfo,
  generateLessonId,
  levelId,
} from "@utils/storage.utils";

const currentLevel = currentLevelInfo();

const defaultForm: LessonFormState = {
  id: "",
  levelId: "",
  title: "",
  description: "",
  lessonNumber: 0,
  exerciseCount: 0,
  goal: "",
  points: [""],
  available: false,
};

const LessonForm = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LessonFormState>(defaultForm);
  const [isSubmitting, setSubmitting] = useState(false);
  const [lessons, setLessons] = useState<{
    isLoading: boolean;
    lessons: LessonFormState[] | [];
  }>({ isLoading: true, lessons: [] });

  // Load existing lesson for editing
  useEffect(() => {
    if (lessonId) {
      fetchLessonById(lessonId)
        .then((res) => {
          if (res.result === null) {
            return showToast("No lesson found at the moment!", false);
          }

          if (res.status === "success") {
            const {
              id,
              levelId,
              title,
              description,
              lessonNumber,
              exerciseCount,
              goal,
              points,
              available,
            } = res.result;
            setFormData({
              id,
              levelId,
              title,
              description,
              lessonNumber,
              exerciseCount,
              goal,
              points,
              available,
            });
          } else {
            showToast("Could not load lesson data", false);
            return null;
          }
        })
        .catch((error) => {
          const message =
            error instanceof Error
              ? error?.message
              : "Apologies an internal server error occurred!";
          showToast(message, false);
          return null;
        });
    }
  }, [lessonId]);

  // Fetching lessons for the current level
  useEffect(() => {
    if (!levelId) return;
    setLessons((prev) => ({ ...prev, isLoading: true }));

    fetchLessonsByLevel(currentLevel.levelId as string, currentLanguage)
      .then((data) => setLessons({ isLoading: false, lessons: data.result }))
      .catch((error) => {
        const message =
          error instanceof Error
            ? error?.message
            : "Apologies an internal server error occurred!";
        showToast(message, false);
        setLessons({ isLoading: false, lessons: [] });
        return;
      });
  }, [levelId]);

  // Function for removing a point
  const removePoint = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      points: prev.points.filter((_, idx) => idx !== index),
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const val =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "lessonNumber" || name === "exerciseCount"
          ? parseInt(value)
          : val,
    }));
  };

  const handlePointChange = (index: number, value: string) => {
    const updated = [...formData.points];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, points: updated }));
  };

  const addPoint = () => {
    setFormData((prev) => ({ ...prev, points: [...prev.points, ""] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    if (!["admin", "editor"].includes(userRole)) {
      showToast("You don't have permission to set level availability", false);
      return;
    }

    formData.levelId = currentLevel.levelId as string;
    const { language, ...formDataWithOutLanguage } = formData;

    if (!lessonId) {
      const existingLessonCount = lessons.lessons.length;
      if (existingLessonCount >= 10) {
        showToast("Maximum of 10 lessons per level reached", false);
        setSubmitting(false);
        return;
      }

      const newLessonId = generateLessonId(
        languageMap[currentLanguage as keyof typeof languageMap]
      );

      if (!newLessonId || newLessonId === null) {
        return showToast(
          "Lesson id not found, please refresh the page and try again",
          false
        );
      }

      formDataWithOutLanguage.id = newLessonId;
    }

    try {
      const endpoint = lessonId
        ? `${API_URL}/lessons/${lessonId}`
        : `${API_URL}/lessons`;

      const response = await postRequest<CreateLessonApiResponse>(
        endpoint,
        formDataWithOutLanguage,
        lessonId ? "PATCH" : "POST"
      );

      if (response.status !== "success") {
        showToast("Failed to save lesson", false);
        return;
      }

      if (!lessonId) {
        showToast(
          lessonId
            ? "Lesson updated successfully!"
            : "Lesson created successfully!",
          true
        );
        setFormData(defaultForm);
        return navigate("/dashboard/levels/lessons");
      }

      return showToast(
        lessonId
          ? "Lesson updated successfully!"
          : "Lesson created successfully!",
        true
      );
    } catch (error: any) {
      showToast(error?.message || "Server error occurred!", false);
    } finally {
      setSubmitting(false);
    }
  };

  if (lessons.isLoading) {
    return (
      <>
        <p className="mt-4 item-center font-bold text-gray-400">
          Loading Foam....
        </p>
      </>
    );
  }

  return (
    <Layout>
      <form
        onSubmit={handleSubmit}
        className="max-w-xl mx-auto mt-8 p-6 bg-white rounded-lg shadow space-y-5"
      >
        <h2 className="text-lg font-bold text-center text-gray-800">
          {lessonId ? "Edit Lesson" : "Create New Lesson"}
        </h2>

        <div>
          <label className="form-label">Language</label>
          <input
            type="text"
            name="language"
            disabled
            value={currentLanguage}
            required
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">Level</label>
          <input
            type="text"
            disabled
            value={`${currentLevel.level} - ${currentLevel.title}`}
            required
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            required
            className="input-style"
          />
        </div>

        <div>
          <label className="form-label">Goal</label>
          <input
            type="text"
            name="goal"
            value={formData.goal}
            onChange={handleChange}
            required
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">Lesson Number</label>
          <input
            type="number"
            name="lessonNumber"
            value={formData.lessonNumber}
            onChange={handleChange}
            required
            className="form-input"
          />
        </div>

        <div>
          <label className="form-label">Exercise Count</label>
          <input
            type="number"
            name="exerciseCount"
            value={formData.exerciseCount}
            onChange={handleChange}
            required
            className="form-input"
          />
        </div>

        <div className="flex items-center gap-2">
          {["admin", "editor"].includes(userRole) && (
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => {
                    setFormData({ ...formData, available: e.target.checked });
                  }}
                />
                <span>Available</span>
              </label>
            </div>
          )}
        </div>

        <div>
          <label className="form-label">Key Points</label>
          <div className="space-y-2">
            {/* Point input and remove button */}
            {formData.points.map((pt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={pt}
                  onChange={(e) => handlePointChange(i, e.target.value)}
                  placeholder={`Point ${i + 1}`}
                  className="form-input flex-grow"
                />
                <button
                  type="button"
                  onClick={() => removePoint(i)}
                  className="text-red-500 hover:text-red-700"
                  title="Remove point"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addPoint}
              className="text-brand text-sm hover:underline"
            >
              + Add another point
            </button>
          </div>
        </div>

        <button type="submit" className="button-primary">
          {isSubmitting
            ? lessonId
              ? "Updating Lesson..."
              : "Creating Lesson..."
            : lessonId
            ? "Update Lesson"
            : "Create Lesson"}
        </button>
      </form>
    </Layout>
  );
};

export default LessonForm;
